import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
    ChevronDownIcon,
    ChevronRightIcon,
    ChevronLeftIcon,
    ArrowsRightLeftIcon,
    SunIcon,
    MoonIcon,
    ViewfinderCircleIcon,
} from '@heroicons/react/24/outline';
import type { Theme } from '../contexts/ThemeContext';
import { resolvePageTitle } from '../config/pageTitles';
import CompanySelector from './CompanySelector';
import ImpersonationBar from './ImpersonationBar';

export interface NavLinkItem {
    path: string;
    label: string;
    minRole: string;
    icon: React.ComponentType<{ className?: string }>;
}

export interface NavGroupItem {
    label: string;
    minRole: string;
    icon: React.ComponentType<{ className?: string }>;
    children: NavLinkItem[];
    /**
     * Optional own path for a group that is itself a navigable section with
     * sub-routes (e.g. Clusters → /clusters/*). When set, the group header
     * highlights as active on the exact path or any `<path>/...` sub-route,
     * matching NavLinkRow's wildcard-subroute behaviour.
     */
    path?: string;
}

export type NavItem = NavLinkItem | NavGroupItem;

export function isNavGroup(item: NavItem): item is NavGroupItem {
    return 'children' in item;
}

function canView(userRole: string, minRole: string): boolean {
    if (minRole === 'admin') return userRole === 'admin';
    if (minRole === 'eskimi_admin') return userRole === 'eskimi_admin' || userRole === 'adops' || userRole === 'admin';
    return true;
}

function filterNavItems(items: NavItem[], userRole: string): NavItem[] {
    return items
        .map((item) => {
            if (isNavGroup(item)) {
                const visibleChildren = item.children.filter((c) => canView(userRole, c.minRole));
                if (visibleChildren.length === 0) return null;
                return { ...item, children: visibleChildren } as NavGroupItem;
            }
            return canView(userRole, item.minRole) ? item : null;
        })
        .filter((item): item is NavItem => item !== null);
}

export interface SidebarUser {
    name: string;
    email: string;
}

interface AppSidebarProps {
    isOpen: boolean;
    onToggle: () => void;
    items: NavItem[];
    userRole: string;
    currentPath: string;
    onNavigate: (path: string) => void;
    theme: Theme;
    onToggleTheme: () => void;
    user: SidebarUser | null;
    onLogout: () => void;
    canImpersonate: boolean;
    onSwitchUser: () => void;
}

// Locked design (option B): a selected submenu child gets no background
// pill — just accent text plus this 3px accent bar on its left edge, so it
// no longer looks identical to a selected top-level item.
const ChildActiveTick: React.FC = () => (
    <span
        aria-hidden="true"
        data-testid="active-child-indicator"
        className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-full bg-accent"
    />
);

// How long the pointer must sit on a nav item before its subtitle flyout
// appears — hover-intent, not instant, so a quick pass over the rail doesn't
// pop a tooltip on every item. Hiding is immediate (no fade-out delay): see
// HoverTooltip's onMouseLeave.
const HOVER_TOOLTIP_DELAY_MS = 500;

// Locked design (option D, deepcontext-wa82): the per-page subtitle that
// used to live in AppLayout's content header now surfaces as a flyout to
// the right of the matching nav item, styled like the existing dark-panel
// pattern (bg-sidebar, border-line, rounded, shadow). Only rendered for the
// expanded desktop rail — the collapsed rail already has a native `title`
// tooltip on its icon buttons, and mobile has no hover — so `text` is only
// ever passed a value from those two call sites.
//
// Portalled to document.body rather than positioned inline: AppLayout wraps
// the rail in an `overflow-hidden` div (needed for its width-collapse
// transition), which would clip an absolutely-positioned flyout the moment
// it crossed the rail's right edge.
const HoverTooltip: React.FC<{ text?: string; children: React.ReactNode }> = ({ text, children }) => {
    const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => () => {
        if (timerRef.current) clearTimeout(timerRef.current);
    }, []);

    if (!text) return <>{children}</>;

    const handleMouseEnter = () => {
        timerRef.current = setTimeout(() => {
            const rect = wrapperRef.current?.getBoundingClientRect();
            if (rect) {
                setCoords({ top: rect.top + rect.height / 2, left: rect.right + 8 });
            }
        }, HOVER_TOOLTIP_DELAY_MS);
    };
    const handleMouseLeave = () => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
        setCoords(null);
    };

    return (
        <div ref={wrapperRef} className="relative" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
            {children}
            {coords &&
                createPortal(
                    <div
                        role="tooltip"
                        style={{ position: 'fixed', top: coords.top, left: coords.left, transform: 'translateY(-50%)' }}
                        className="z-50 whitespace-nowrap rounded-lg border border-line bg-sidebar px-3 py-2 text-xs font-normal text-ink shadow-lg"
                    >
                        {text}
                    </div>,
                    document.body
                )}
        </div>
    );
};

// Wraps a nav row in the hover flyout when `showTooltip` is set and the
// route has a subtitle in PAGE_TITLES; otherwise renders the row unchanged.
function renderNavLink(
    item: NavLinkItem,
    currentPath: string,
    onNavigate: (path: string) => void,
    iconOnly: boolean,
    showTooltip: boolean,
    isChild = false
): React.ReactElement {
    const subtitle = showTooltip ? resolvePageTitle(item.path)?.subtitle : undefined;
    return (
        <HoverTooltip text={subtitle}>
            {NavLinkRow(item, currentPath, onNavigate, iconOnly, isChild)}
        </HoverTooltip>
    );
}

function NavLinkRow(
    item: NavLinkItem,
    currentPath: string,
    onNavigate: (path: string) => void,
    iconOnly: boolean,
    isChild = false
): React.ReactElement {
    const isActive = currentPath === item.path || currentPath.startsWith(item.path + '/');
    const Icon = item.icon;
    const baseClass = `flex items-center rounded-full text-sm transition-colors relative ${
        isActive
            ? isChild
                ? 'text-accent font-semibold'
                : 'bg-nav-active text-accent font-bold'
            : 'text-ink font-medium hover:bg-canvas'
    }`;
    if (iconOnly) {
        return (
            <button
                type="button"
                onClick={() => onNavigate(item.path)}
                className={`p-2.5 justify-center ${baseClass}`}
                title={item.label}
            >
                {isChild && isActive && <ChildActiveTick />}
                <Icon className="h-5 w-5" />
            </button>
        );
    }
    return (
        <button
            type="button"
            onClick={() => onNavigate(item.path)}
            className={`gap-3 w-full px-3 py-2.5 text-left ${baseClass}`}
        >
            {isChild && isActive && <ChildActiveTick />}
            <Icon className="h-5 w-5 flex-shrink-0" />
            <span>{item.label}</span>
        </button>
    );
}

interface NavGroupSectionProps {
    item: NavGroupItem;
    currentPath: string;
    onNavigate: (path: string) => void;
    iconOnly: boolean;
    /** Show the hover-intent subtitle flyout (expanded desktop rail only —
     * omit for the collapsed rail's icon flyout and for MobileNav, which
     * has no hover). */
    showTooltips?: boolean;
}

const NavGroupSection: React.FC<NavGroupSectionProps> = ({
    item,
    currentPath,
    onNavigate,
    iconOnly,
    showTooltips = false,
}) => {
    const matchesOwnPath =
        item.path !== undefined &&
        (currentPath === item.path || currentPath.startsWith(item.path + '/'));
    const hasActiveChild =
        matchesOwnPath || item.children.some((c) => c.path === currentPath);
    const [expanded, setExpanded] = useState(hasActiveChild);
    useEffect(() => {
        if (hasActiveChild) setExpanded(true);
    }, [hasActiveChild]);
    const Icon = item.icon;

    if (iconOnly) {
        return (
            <div className="space-y-0.5">
                <button
                    type="button"
                    onClick={() => setExpanded((e) => !e)}
                    className={`flex items-center justify-center w-full p-2.5 rounded-full text-sm transition-colors ${
                        hasActiveChild ? 'text-accent bg-nav-active font-bold' : 'text-ink font-medium hover:bg-canvas'
                    }`}
                    title={item.label}
                >
                    <Icon className="h-5 w-5" />
                </button>
                {expanded && (
                    <div className="rounded-2xl bg-canvas py-1 space-y-0.5">
                        {item.children.map((child) => {
                            const isChildActive = currentPath === child.path;
                            return (
                                <button
                                    key={child.path}
                                    type="button"
                                    onClick={() => onNavigate(child.path)}
                                    className={`relative flex items-center justify-center w-full p-2 rounded-full text-sm transition-colors ${
                                        isChildActive
                                            ? 'text-accent font-semibold'
                                            : 'text-ink hover:bg-surface'
                                    }`}
                                    title={child.label}
                                >
                                    {isChildActive && <ChildActiveTick />}
                                    <child.icon className="h-5 w-5" />
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    }

    const headerSubtitle = showTooltips ? resolvePageTitle(item.path ?? '')?.subtitle : undefined;

    return (
        <div className="space-y-1">
            <HoverTooltip text={headerSubtitle}>
                <button
                    type="button"
                    onClick={() => setExpanded((e) => !e)}
                    className={`flex items-center gap-3 w-full px-3 py-2.5 rounded-full text-left text-sm transition-colors ${
                        hasActiveChild ? 'text-accent bg-nav-active font-bold' : 'text-ink font-medium hover:bg-canvas'
                    }`}
                >
                    <Icon className="h-5 w-5 flex-shrink-0" />
                    <span className="flex-1">{item.label}</span>
                    {expanded ? (
                        <ChevronDownIcon className="h-4 w-4 flex-shrink-0" />
                    ) : (
                        <ChevronRightIcon className="h-4 w-4 flex-shrink-0" />
                    )}
                </button>
            </HoverTooltip>
            {expanded && (
                <div className="pl-4 space-y-0.5">
                    {item.children.map((child) => (
                        <div key={child.path}>
                            {renderNavLink(child, currentPath, onNavigate, false, showTooltips, true)}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

interface SidebarBrandProps {
    expanded: boolean;
    onNavigate: (path: string) => void;
}

const SidebarBrand: React.FC<SidebarBrandProps> = ({ expanded, onNavigate }) => (
    <button
        type="button"
        onClick={() => onNavigate('/')}
        aria-label="DeepContext"
        className={`flex flex-shrink-0 items-center gap-2 px-3 py-3 ${expanded ? '' : 'justify-center'}`}
    >
        <ViewfinderCircleIcon className={`h-7 w-7 flex-shrink-0 text-accent ${expanded ? 'ml-1' : ''}`} />
        {expanded && (
            <span className="whitespace-nowrap text-lg font-extrabold text-ink">
                Deep<span className="text-accent">Context</span>
            </span>
        )}
    </button>
);

interface SidebarAccountFooterProps {
    expanded: boolean;
    theme: Theme;
    onToggleTheme: () => void;
    user: SidebarUser | null;
    onLogout: () => void;
    canImpersonate: boolean;
    onSwitchUser: () => void;
}

// Theme toggle + user chip (with its logout/switch-user menu) — the part of
// the old Header's chrome that must stay reachable at every viewport width,
// not just lg+. Shared by the desktop rail (SidebarFooter) and MobileNav so
// phones/narrow tablets keep a way to log out, toggle theme, or switch the
// impersonated user once the topbar is gone.
const SidebarAccountFooter: React.FC<SidebarAccountFooterProps> = ({
    expanded,
    theme,
    onToggleTheme,
    user,
    onLogout,
    canImpersonate,
    onSwitchUser,
}) => {
    const [menuOpen, setMenuOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);
    const isDark = theme === 'dark';

    useEffect(() => {
        if (!menuOpen) return;
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [menuOpen]);

    return (
        <div className="space-y-0.5">
            <button
                type="button"
                onClick={onToggleTheme}
                aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                aria-pressed={isDark}
                className="flex w-full items-center justify-center rounded-full py-2 text-ink-muted transition-colors hover:bg-canvas"
            >
                {isDark ? <MoonIcon className="h-4 w-4" /> : <SunIcon className="h-4 w-4" />}
            </button>

            {user && (
                <div className="relative" ref={menuRef}>
                    <button
                        type="button"
                        onClick={() => setMenuOpen((o) => !o)}
                        className={`flex w-full items-center gap-2.5 rounded-full text-left transition-colors hover:bg-canvas ${expanded ? 'px-3 py-1.5' : 'justify-center py-1.5'}`}
                    >
                        <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-accent text-xs font-medium text-white">
                            {user.name.charAt(0).toUpperCase()}
                        </span>
                        {expanded && (
                            <span className="min-w-0 flex-1">
                                <span className="block truncate text-xs font-bold text-ink">{user.name}</span>
                                <span className="block truncate text-xs text-ink-muted">{user.email}</span>
                            </span>
                        )}
                    </button>

                    {menuOpen && (
                        <div className="absolute bottom-full left-0 z-50 mb-2 w-56 rounded-md border border-line bg-surface py-1 shadow-lg">
                            <div className="border-b border-line px-4 py-2">
                                <p className="text-sm font-medium text-ink">{user.name}</p>
                                <p className="text-xs text-ink-muted">{user.email}</p>
                            </div>
                            {canImpersonate && (
                                <button
                                    type="button"
                                    onClick={() => { setMenuOpen(false); onSwitchUser(); }}
                                    className="block w-full border-b border-line px-4 py-2 text-left text-sm text-ink hover:bg-canvas"
                                >
                                    <span className="flex items-center">
                                        <ArrowsRightLeftIcon className="mr-2 h-4 w-4" />
                                        <span>Switch User View</span>
                                    </span>
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={() => { setMenuOpen(false); onLogout(); }}
                                className="block w-full px-4 py-2 text-left text-sm text-ink hover:bg-canvas"
                            >
                                Sign out
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

interface SidebarFooterProps extends SidebarAccountFooterProps {
    onToggleCollapse: () => void;
}

// Desktop rail footer: the account cluster above, plus the rail
// expand/collapse control — a rail-only concept, so it does not belong in
// MobileNav's reuse of SidebarAccountFooter.
const SidebarFooter: React.FC<SidebarFooterProps> = ({ onToggleCollapse, ...accountProps }) => {
    const { expanded } = accountProps;
    return (
        <div className="mt-auto flex-shrink-0 space-y-0.5 border-t border-line px-1 pb-2 pt-2">
            <ImpersonationBar expanded={expanded} />
            <SidebarAccountFooter {...accountProps} />

            <button
                type="button"
                onClick={onToggleCollapse}
                aria-label={expanded ? 'Collapse sidebar' : 'Expand sidebar'}
                className={`flex w-full items-center gap-2.5 rounded-full text-xs font-semibold text-ink-muted transition-colors hover:bg-canvas ${expanded ? 'px-3 py-1.5' : 'justify-center py-1.5'}`}
            >
                <ChevronLeftIcon className={`h-4 w-4 flex-shrink-0 transition-transform ${expanded ? '' : 'rotate-180'}`} />
                {expanded && <span>Collapse</span>}
            </button>
        </div>
    );
};

const AppSidebar: React.FC<AppSidebarProps> = ({
    isOpen,
    onToggle,
    items,
    userRole,
    currentPath,
    onNavigate,
    theme,
    onToggleTheme,
    user,
    onLogout,
    canImpersonate,
    onSwitchUser,
}) => {
    const visible = filterNavItems(items, userRole);
    const expanded = isOpen;

    const sidebarContent = (
        <>
            <SidebarBrand expanded={expanded} onNavigate={onNavigate} />
            <div className="flex-shrink-0 px-2 pb-2">
                <CompanySelector expanded={expanded} />
            </div>
            <nav className="flex-1 overflow-y-auto pb-4 px-2 space-y-1">
                {visible.map((item) =>
                    isNavGroup(item) ? (
                        <div key={`group-${item.label}`}>
                            <NavGroupSection
                                item={item}
                                currentPath={currentPath}
                                onNavigate={onNavigate}
                                iconOnly={!expanded}
                                showTooltips={expanded}
                            />
                        </div>
                    ) : (
                        <div key={item.path}>
                            {renderNavLink(item, currentPath, onNavigate, !expanded, expanded)}
                        </div>
                    )
                )}
            </nav>
            <SidebarFooter
                expanded={expanded}
                theme={theme}
                onToggleTheme={onToggleTheme}
                user={user}
                onLogout={onLogout}
                canImpersonate={canImpersonate}
                onSwitchUser={onSwitchUser}
                onToggleCollapse={onToggle}
            />
        </>
    );

    return (
        <aside
            className={`bg-sidebar border-r border-line flex flex-col flex-shrink-0 h-full min-h-0 w-full transition-[width] duration-200`}
            aria-label="Main navigation"
        >
            {sidebarContent}
        </aside>
    );
};

interface MobileNavProps {
    isOpen: boolean;
    onClose: () => void;
    items: NavItem[];
    userRole: string;
    currentPath: string;
    onNavigate: (path: string) => void;
    anchorRef: React.RefObject<HTMLDivElement | null>;
    theme: Theme;
    onToggleTheme: () => void;
    user: SidebarUser | null;
    onLogout: () => void;
    canImpersonate: boolean;
    onSwitchUser: () => void;
}

export const MobileNav: React.FC<MobileNavProps> = ({
    isOpen, onClose, items, userRole, currentPath, onNavigate, anchorRef,
    theme, onToggleTheme, user, onLogout, canImpersonate, onSwitchUser,
}) => {
    if (!isOpen) return null;

    const visible = filterNavItems(items, userRole);
    const top = anchorRef.current?.getBoundingClientRect().bottom ?? 0;

    return (
        <>
            <div
                className="fixed inset-0 bg-black/20 z-30 lg:hidden"
                onClick={onClose} role="button" tabIndex={-1} aria-label="Close menu"
            />
            <nav
                className="fixed left-0 right-0 z-40 bg-surface shadow-lg border-t border-line overflow-y-auto lg:hidden"
                style={{ top, maxHeight: `calc(100vh - ${top}px - 2rem)` }}
                aria-label="Mobile navigation"
            >
                {/* Same reasoning as the theme/user/logout controls below: the
                    desktop rail (and its CompanySelector) is hidden entirely
                    below lg, so this is the only place phones/narrow tablets
                    can switch company. */}
                <div className="px-3 pt-3">
                    <CompanySelector />
                </div>
                <MobileNavItems items={visible} currentPath={currentPath} onNavigate={onNavigate} />
                {/* Below the lg breakpoint the sidebar rail is hidden entirely
                    (AppLayout wraps it in `hidden lg:flex`), so this is the
                    only place phones/narrow tablets can reach the theme
                    toggle, user chip, logout, or switch-user — closing the
                    drawer on those actions mirrors onNavigate's close-on-tap. */}
                <div className="border-t border-line px-3 pb-3 pt-2">
                    <ImpersonationBar />
                    <SidebarAccountFooter
                        expanded
                        theme={theme}
                        onToggleTheme={onToggleTheme}
                        user={user}
                        onLogout={() => { onClose(); onLogout(); }}
                        canImpersonate={canImpersonate}
                        onSwitchUser={() => { onClose(); onSwitchUser(); }}
                    />
                </div>
            </nav>
        </>
    );
};

function MobileNavItems({ items, currentPath, onNavigate }: {
    items: NavItem[]; currentPath: string; onNavigate: (path: string) => void;
}) {
    return (
        <div className="py-2 px-3 space-y-1">
            {items.map((item) =>
                isNavGroup(item) ? (
                    <NavGroupSection
                        key={`group-${item.label}`}
                        item={item}
                        currentPath={currentPath}
                        onNavigate={onNavigate}
                        iconOnly={false}
                    />
                ) : (
                    <div key={item.path}>
                        {NavLinkRow(item, currentPath, onNavigate, false)}
                    </div>
                )
            )}
        </div>
    );
}

export default AppSidebar;
